/**
 * Page Assets Service
 * 
 * Service for uploading and managing media assets specifically for page/view components.
 * Assets are stored in team-isolated MinIO buckets, separate from the content library.
 */

import axios from 'axios';
import { environment } from '../../environments/environment';

export interface PageAsset {
  id: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  assetType: 'image' | 'video' | 'icon';
  teamId: string;
  url?: string;
  uploadedAt: string;
  uploadedBy: string;
  organizationId?: string;
}

export interface PageAssetUploadResponse {
  data: PageAsset;
  metadata: {
    message: string;
    timestamp: string;
  };
}

export interface PageAssetsListResponse {
  data: PageAsset[];
  metadata: {
    pagination: {
      totalCount: number;
      pageSize: number;
      currentPage: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
    timestamp: string;
  };
}

export interface PageAssetGetResponse {
  data: PageAsset;
  metadata: {
    timestamp: string;
  };
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export class PageAssetsService {
  private baseURL: string;

  constructor() {
    this.baseURL = environment.apiUrl;
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return {
      'Authorization': `Bearer ${token}`,
    };
  }

  /**
   * Upload a page asset file
   */
  async uploadAsset(
    file: File,
    teamId: string,
    organizationId?: string,
    assetType: 'image' | 'video' | 'icon' = 'image',
    onProgress?: (progress: UploadProgress) => void
  ): Promise<PageAsset> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('teamId', teamId);
      if (organizationId) {
        formData.append('organizationId', organizationId);
      }
      formData.append('assetType', assetType);

      const response = await axios.post<PageAssetUploadResponse>(
        `${this.baseURL}/page-assets/upload`,
        formData,
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent: any) => {
            if (onProgress && progressEvent.total) {
              const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress({
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                percentage,
              });
            }
          },
        } as any
      );

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to upload page asset:', error);
      throw new Error(
        error.response?.data?.error?.message || 
        error.response?.data?.message || 
        'Failed to upload page asset'
      );
    }
  }

  /**
   * Get a specific page asset
   */
  async getAsset(teamId: string, assetId: string): Promise<PageAsset> {
    try {
      const response = await axios.get<PageAssetGetResponse>(
        `${this.baseURL}/page-assets/${teamId}/${assetId}`,
        { headers: this.getAuthHeaders() }
      );

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to get page asset:', error);
      throw new Error(
        error.response?.data?.error?.message || 
        error.response?.data?.message || 
        'Failed to get page asset'
      );
    }
  }

  /**
   * List page assets for a team
   */
  async listAssets(
    teamId: string,
    options: {
      assetType?: 'image' | 'video' | 'icon';
      page?: number;
      limit?: number;
    } = {}
  ): Promise<{ assets: PageAsset[]; pagination: PageAssetsListResponse['metadata']['pagination'] }> {
    try {
      const params = new URLSearchParams();
      if (options.assetType) params.append('assetType', options.assetType);
      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());

      const response = await axios.get<PageAssetsListResponse>(
        `${this.baseURL}/page-assets/${teamId}?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );

      return {
        assets: response.data.data,
        pagination: response.data.metadata.pagination,
      };
    } catch (error: any) {
      console.error('Failed to list page assets:', error);
      throw new Error(
        error.response?.data?.error?.message || 
        error.response?.data?.message || 
        'Failed to list page assets'
      );
    }
  }

  /**
   * Delete a page asset
   */
  async deleteAsset(teamId: string, assetId: string): Promise<void> {
    try {
      await axios.delete(
        `${this.baseURL}/page-assets/${teamId}/${assetId}`,
        { headers: this.getAuthHeaders() }
      );
    } catch (error: any) {
      console.error('Failed to delete page asset:', error);
      throw new Error(
        error.response?.data?.error?.message || 
        error.response?.data?.message || 
        'Failed to delete page asset'
      );
    }
  }

  /**
   * Check if a file is a valid page asset type
   */
  isValidAssetType(file: File): boolean {
    const allowedTypes = [
      'image/jpeg',
      'image/png', 
      'image/gif',
      'image/webp',
      'image/svg+xml',
      'video/mp4',
      'video/webm',
      'video/avi',
      'video/quicktime'
    ];
    
    return allowedTypes.includes(file.type);
  }

  /**
   * Get the asset type based on file MIME type
   */
  getAssetType(file: File): 'image' | 'video' | 'icon' {
    if (file.type.startsWith('video/')) {
      return 'video';
    } else if (file.type === 'image/svg+xml') {
      return 'icon';
    } else {
      return 'image';
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'K', 'M', 'G'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (bytes / Math.pow(k, i)).toFixed(1) + sizes[i];
  }

  /**
   * Validate file size (50MB limit for page assets)
   */
  isValidFileSize(file: File): boolean {
    const maxSize = 50 * 1024 * 1024; // 50MB
    return file.size <= maxSize;
  }
}

// Export singleton instance
export const pageAssetsService = new PageAssetsService();
export default pageAssetsService;